import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import './impact-overview.css';
import { getUseCaseService } from '../../services/UseCaseService';
import { riskColor, riskLabel, RiskPlusTopNav, RiskPlusPagerNav, RiskPlusPagerLink } from '../risk-plus/RiskPlusPageShell';
import { ImpactPlusReportStore } from '../../stores/ImpactPlusReportStore';
import { ImpactReportDetailModal } from '../ImpactPlusReport/ImpactReportModal';
import { useReportModalParam } from '../../hooks/useReportModalParam';
import type { ImpactPlusReport, StakeholderCategory } from '../../entities/report/schemas/ImpactPlusReport.schema';

const CAT_LABEL: Record<StakeholderCategory, string> = {
  people: 'People',
  organisation: 'Organisation',
  ecosystem: 'Ecosystem',
};

const ImpactPlusHazards = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const impactPlusReportId = searchParams.get('impactPlusReportId') ?? '';
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const impactPlusReportQuery = impactPlusReportId
    ? `?impactPlusReportId=${encodeURIComponent(impactPlusReportId)}`
    : '';

  const setImpactPlusReportInStore = ImpactPlusReportStore.useImpactPlusReportStore((s) => s.setImpactPlusReport);

  const [impactPlusReport, setImpactPlusReport] = useState<ImpactPlusReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const impactReportModal = useReportModalParam('impactPlusReportModal');

  const loadImpactPlusReport = useCallback(async () => {
    if (!impactPlusReportId) {
      setImpactPlusReport(null);
      setReportError(null);
      setIsLoadingReport(false);
      return;
    }
    setReportError(null);
    setIsLoadingReport(true);
    try {
      const report = await getUseCaseService().getImpactPlusReport(impactPlusReportId);
      setImpactPlusReport(report);
    } catch (err) {
      setImpactPlusReport(null);
      setReportError(err instanceof Error ? err.message : 'Could not load Impact+ report.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [impactPlusReportId]);

  useEffect(() => {
    void loadImpactPlusReport();
  }, [loadImpactPlusReport]);

  useEffect(() => {
    if (impactReportModal.isOpen && impactPlusReport) {
      setImpactPlusReportInStore(impactPlusReport);
    }
  }, [impactReportModal.isOpen, impactPlusReport, setImpactPlusReportInStore]);

  const cohorts = impactPlusReport?.impactedStakeholders.impactedCohorts ?? [];

  const cohortRankSplit = useMemo(() => {
    const rankings = impactPlusReport?.decisionGuidance.cohortRankings ?? [];
    return rankings
      .filter((r) => r.numCohorts > 0)
      .slice()
      .sort((a, b) => b.riskRankNum - a.riskRankNum)
      .map((r) => ({ n: r.numCohorts, label: riskLabel(r.riskRankLabel), dot: riskColor(r.riskRankLabel) }));
  }, [impactPlusReport]);

  const topImpacts = useMemo(() => {
    const ranked = [...cohorts].sort(
      (a, b) =>
        (impactPlusReport?.decisionGuidance.cohortRankings.find((r) => r.riskRankLabel === b.cohortRisk)?.riskRankNum ?? 0) -
        (impactPlusReport?.decisionGuidance.cohortRankings.find((r) => r.riskRankLabel === a.cohortRisk)?.riskRankNum ?? 0),
    );
    return ranked.slice(0, 5);
  }, [cohorts, impactPlusReport]);

  const topImpactsNote = `The five most severe impacts, ranked from ${cohorts.length} assessed stakeholder cohort${cohorts.length === 1 ? '' : 's'}. Open any row for the full assessment.`;

  const impactAssessmentHref = `/use-case/${encodeURIComponent(useCaseChatId)}/impact-assessment${impactPlusReportQuery}`;
  const stakeholderRegisterHref = `/use-case/${encodeURIComponent(useCaseChatId)}/stakeholder-register${impactPlusReportQuery}`;

  if (!useCaseChatId) return null;

  return (
    <div className="iovw-page min-h-screen pb-10 pt-6">
      <div className="mx-auto max-w-[1180px] px-4 md:px-6">
        <RiskPlusTopNav
          useCaseChatId={useCaseChatId}
          secondaryNav={
            <RiskPlusPagerNav>
              <RiskPlusPagerLink to={stakeholderRegisterHref} label="Stakeholder Register" direction="next" />
            </RiskPlusPagerNav>
          }
        />

        {impactPlusReportId && isLoadingReport && !impactPlusReport && (
          <div className="mt-4 animate-pulse space-y-3">
            <div className="h-24 rounded-xl bg-gray-100" />
            <div className="h-20 rounded-xl bg-gray-100" />
            <div className="h-48 rounded-xl bg-gray-100" />
          </div>
        )}

        {impactPlusReportId && !isLoadingReport && reportError && !impactPlusReport && (
          <div
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Impact+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadImpactPlusReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!impactPlusReportId && !isLoadingReport && (
          <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center shadow-xs">
            <p className="text-sm font-medium text-midnight-navi">No Impact+ report selected</p>
            <p className="mt-1 text-xs text-gray-500">Generate an Impact+ report for this use case to see the impact overview.</p>
          </div>
        )}

        {impactPlusReport && (
          <div className="iovw mt-6">
            <div className="iovw-verdict">
              <div className="iovw-verdict-row">
                <div className="iovw-verdict-main">
                  <div className="iovw-verdict-eyebrow">Impact Overview · Impact+ outcome</div>
                  {impactPlusReport.decisionGuidance.verdictLabel && (
                    <h3 className="iovw-verdict-text">{impactPlusReport.decisionGuidance.verdictLabel}</h3>
                  )}
                  <div className="iovw-verdict-rank">
                    <span className="iovw-rank-chip">
                      <span className="iovw-sev-dot" style={{ background: riskColor(impactPlusReport.overallRiskRank) }} />
                      <span>{riskLabel(impactPlusReport.overallRiskRank)} impact</span>
                    </span>
                  </div>
                </div>

                <div className="iovw-verdict-reports">
                  <div className="iovw-verdict-reports-label">Reports</div>
                  <button
                    type="button"
                    className="iovw-verdict-reports-link"
                    onClick={() => impactReportModal.open(impactPlusReportId)}
                  >
                    <FileText className="h-4 w-4" strokeWidth={2} />
                    Impact+ Report
                  </button>
                  {impactPlusReport.isStale && (
                    <span className="iovw-verdict-reports-cap">
                      Context was updated in the intake after this report ran.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="iovw-card">
              <div className="iovw-card-hdr iovw-tbl-hdr">
                <span className="iovw-card-title">Impact across stakeholder cohorts</span>
              </div>
              <div className="iovw-card-pad">
                <div className="iovw-rank-split">
                  {cohortRankSplit.map((r, i) => (
                    <div key={i} className="iovw-rank-pill">
                      <span className="iovw-rank-pill-num">{r.n}</span>
                      <span className="iovw-rank-pill-lbl">
                        <span className="iovw-sev-dot" style={{ background: r.dot }} />
                        {r.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="iovw-count-line">{impactPlusReport.decisionGuidance.cohortStatsSummary}</div>
              </div>
            </div>

            {impactPlusReport.decisionGuidance.executiveBullets.length > 0 && (
              <div className="iovw-card iovw-card-tinted">
                <div className="iovw-card-hdr">
                  <span className="iovw-card-title">What leadership should weigh</span>
                </div>
                <div className="iovw-card-pad" style={{ paddingTop: 0 }}>
                  <ol className="iovw-interp">
                    {impactPlusReport.decisionGuidance.executiveBullets.map((text, i) => (
                      <li key={i}>
                        <span className="iovw-interp-num">{i + 1}</span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            <div className="iovw-section-eyebrow">Top impacts</div>
            <div className="iovw-scope-band" style={{ marginBottom: 16 }}>
              <span className="aq-icon" aria-hidden>info</span>
              <div className="iovw-scope-band-text">{topImpactsNote}</div>
            </div>

            {topImpacts.map((c, i) => (
              <div
                key={i}
                className={i === 0 ? 'iovw-ti-lead' : 'iovw-ti-row'}
                onClick={() => navigate(impactAssessmentHref)}
              >
                <div className="iovw-ti-rank" style={{ color: riskColor(c.cohortRisk) }}>
                  {i + 1}
                </div>
                <div>
                  <div className="iovw-ti-top">
                    <span className="iovw-ti-name">{c.label}</span>
                    <span className="iovw-sev-chip">
                      <span className="iovw-sev-dot" style={{ background: riskColor(c.cohortRisk) }} />
                      {riskLabel(c.cohortRisk)}
                    </span>
                    <span className="iovw-cat-tag">{CAT_LABEL[c.stakeholderCategory]}</span>
                  </div>
                  <div className="iovw-ti-sum">{c.harmSummary}</div>
                </div>
              </div>
            ))}

            {(impactPlusReport.nextSteps.introduction || impactPlusReport.nextSteps.recommendedNextStep) && (
              <div className="iovw-nextstep">
                <span className="aq-icon" aria-hidden>arrow_circle_right</span>
                <div>
                  <div className="iovw-nextstep-title">Recommended next step</div>
                  <div className="iovw-nextstep-body">
                    {impactPlusReport.nextSteps.introduction} {impactPlusReport.nextSteps.recommendedNextStep}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ImpactReportDetailModal
        isOpen={impactReportModal.isOpen}
        onClose={() => {
          impactReportModal.close();
          setImpactPlusReportInStore(null);
        }}
      />
    </div>
  );
};

export default ImpactPlusHazards;
